import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import { makeStyles } from '@material-ui/core/styles';

import { isToday } from 'base/utils/dates';
import {
  fulfillmentOptions,
  ALL_ONLINE_ORDERING_FULFILLMENT_METHODS
} from 'cart/constants/fulfillmentOptions';
import {
  MILITARY_TIME,
  DATE_INPUT_FORMAT,
  FULL_DAY_OUTPUT_FORMAT,
  HOUR_MINUTE_DAY_TIME
} from 'base/constants/timeFormat';
import { WHITE } from 'base/constants/colors';

import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';

import AlertSnackbar from 'base/components/AlertSnackbar';
import { isMobileMedia } from 'utils/media';

const useStyles = makeStyles(theme => ({
  root: {
    color: WHITE,
    fontSize: 16
  }
}));

const NoAsapForPickupAndDeliveryMessage = ({
  pickupTime,
  pickupWeekday,
  deliveryTime,
  deliveryWeekday,
  isPickupToday,
  isDeliveryToday,
  snackbarHeadingOverride
}) => {
  const classes = useStyles();
  return (
    <span>
      <Box pb={1}>
        <Typography component="p" variant="h6" classes={classes}>
          <strong>
            {snackbarHeadingOverride || 'No ASAP Pickup or Delivery'}
          </strong>
        </Typography>
      </Box>
      <Typography component="p" variant="subtitle2">
        {isPickupToday
          ? `Our next pickup time is later today at ${pickupTime}.`
          : `Our next pickup time is ${pickupWeekday} at ${pickupTime}.`}
      </Typography>
      <Typography component="p" variant="subtitle1">
        {isDeliveryToday
          ? `Delivery is next available later today at ${deliveryTime}.`
          : `Delivery is next available ${deliveryWeekday} at ${deliveryTime}.`}
      </Typography>
    </span>
  );
};

NoAsapForPickupAndDeliveryMessage.propTypes = {
  pickupTime: PropTypes.string,
  pickupWeekday: PropTypes.string,
  deliveryTime: PropTypes.string,
  deliveryWeekday: PropTypes.string,
  snackbarHeadingOverride: PropTypes.string,
  isPickupToday: PropTypes.bool,
  isDeliveryToday: PropTypes.bool
};

const NoAsapForPickupMessage = ({
  pickupTime,
  pickupWeekday,
  isPickupToday,
  snackbarHeadingOverride
}) => {
  const classes = useStyles();
  return (
    <span>
      <Typography variant="h6" classes={classes}>
        <strong>{snackbarHeadingOverride || 'No ASAP Pickup'}</strong>
      </Typography>
      <Typography variant="subtitle1">
        {isPickupToday
          ? `Our next pickup time is later today at ${pickupTime}.`
          : `Our next pickup time is ${pickupWeekday} at ${pickupTime}.`}
      </Typography>
    </span>
  );
};

NoAsapForPickupMessage.propTypes = {
  pickupTime: PropTypes.string,
  pickupWeekday: PropTypes.string,
  isPickupToday: PropTypes.bool,
  snackbarHeadingOverride: PropTypes.string
};

const NoAsapForDeliveryMessage = ({
  deliveryWeekday,
  deliveryTime,
  isDeliveryToday,
  snackbarHeadingOverride
}) => {
  const classes = useStyles();
  return (
    <span>
      <Typography variant="h6" classes={classes}>
        <strong>{snackbarHeadingOverride || 'No ASAP Delivery'}</strong>
      </Typography>
      <Typography variant="subtitle1">
        {isDeliveryToday
          ? `Delivery is next available later today at ${deliveryTime}.`
          : `Delivery is next available ${deliveryWeekday} at ${deliveryTime}.`}
      </Typography>
    </span>
  );
};

NoAsapForDeliveryMessage.propTypes = {
  deliveryTime: PropTypes.string,
  deliveryWeekday: PropTypes.string,
  isDeliveryToday: PropTypes.bool,
  snackbarHeadingOverride: PropTypes.string
};

const NoAsapSnackbar = ({
  open,
  onClose,
  pickupTime,
  pickupDate,
  deliveryDate,
  deliveryTime,
  isAsapAvailableForPickup,
  isAsapAvailableForDelivery,
  today,
  cartWorkingCopy,
  availableFulfillmentOptions,
  snackbarHeadingOverride
}) => {
  const isPickupToday = isToday(pickupDate, today);
  const isDeliveryToday = isToday(deliveryDate, today);
  const formattedPickupTime = moment(pickupTime, MILITARY_TIME).format(
    HOUR_MINUTE_DAY_TIME
  );
  const formattedPickupWeekday = moment(pickupDate, DATE_INPUT_FORMAT).format(
    FULL_DAY_OUTPUT_FORMAT
  );
  const formattedDeliveryTime = moment(deliveryTime, MILITARY_TIME).format(
    HOUR_MINUTE_DAY_TIME
  );
  const formattedDeliveryWeekday = moment(
    deliveryDate,
    DATE_INPUT_FORMAT
  ).format(FULL_DAY_OUTPUT_FORMAT);
  const getMessage = () => {
    if (
      !isAsapAvailableForPickup &&
      !isAsapAvailableForDelivery &&
      availableFulfillmentOptions.length === 2
    ) {
      return (
        <NoAsapForPickupAndDeliveryMessage
          pickupTime={formattedPickupTime}
          pickupWeekday={formattedPickupWeekday}
          deliveryTime={formattedDeliveryTime}
          deliveryWeekday={formattedDeliveryWeekday}
          isPickupToday={isPickupToday}
          isDeliveryToday={isDeliveryToday}
          snackbarHeadingOverride={snackbarHeadingOverride}
        />
      );
    } else if (
      !isAsapAvailableForPickup &&
      cartWorkingCopy.online_ordering_fulfillment_method ===
        fulfillmentOptions.PICKUP &&
      availableFulfillmentOptions.includes('pickup')
    ) {
      return (
        <NoAsapForPickupMessage
          pickupTime={formattedPickupTime}
          pickupWeekday={formattedPickupWeekday}
          isPickupToday={isPickupToday}
          snackbarHeadingOverride={snackbarHeadingOverride}
        />
      );
    } else if (
      !isAsapAvailableForDelivery &&
      cartWorkingCopy.online_ordering_fulfillment_method ===
        fulfillmentOptions.DELIVERY &&
      availableFulfillmentOptions.includes('delivery')
    ) {
      return (
        <NoAsapForDeliveryMessage
          deliveryTime={formattedDeliveryTime}
          deliveryWeekday={formattedDeliveryWeekday}
          isDeliveryToday={isDeliveryToday}
          snackbarHeadingOverride={snackbarHeadingOverride}
        />
      );
    }
    return null;
  };
  const message = getMessage();

  if (!message) {
    return null;
  }

  return (
    <AlertSnackbar
      onClose={onClose}
      open={open}
      message={message}
      cartWorkingCopy={cartWorkingCopy}
      isAsapAvailableForPickup={isAsapAvailableForPickup}
      isAsapAvailableForDelivery={isAsapAvailableForDelivery}
      availableFulfillmentOptions={availableFulfillmentOptions}
      vertical={isMobileMedia() ? 'bottom' : 'top'}
    />
  );
};

NoAsapSnackbar.defaultProps = {
  open: false,
  onClose: () => {},
  snackbarHeadingOverride: ''
};

NoAsapSnackbar.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  pickupTime: PropTypes.string,
  pickupDate: PropTypes.string,
  deliveryDate: PropTypes.string,
  deliveryTime: PropTypes.string,
  isAsapAvailableForPickup: PropTypes.bool,
  isAsapAvailableForDelivery: PropTypes.bool,
  today: PropTypes.string,
  cartWorkingCopy: PropTypes.object,
  availableFulfillmentOptions: PropTypes.arrayOf(
    PropTypes.oneOf(ALL_ONLINE_ORDERING_FULFILLMENT_METHODS)
  ),
  snackbarHeadingOverride: PropTypes.string
};

export default NoAsapSnackbar;
